import type { Notification } from '@/types/database.types';
import { dashboardPathForRole, type Role } from '@/types/roles';

const SHIFT_LIST_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/shifts',
  BM: '/bm/shifts',
  MANAGER: '/manager/shifts',
  EMPLOYEE: '/employee/my-shifts',
};

const INVOICE_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/invoices',
  BM: '/bm/invoices',
  MANAGER: '/manager/dashboard',
  EMPLOYEE: '/employee/invoices',
};

const COMMUNICATION_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/communication',
  BM: '/bm/communication',
  MANAGER: '/manager/communication',
  EMPLOYEE: '/employee/communication',
};

const APPROVAL_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/approvals/timesheets',
  BM: '/bm/approvals/timesheets',
  MANAGER: '/manager/approvals/timesheets',
  EMPLOYEE: '/employee/leave',
};

const LEAVE_APPROVAL_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/approvals/leave',
  BM: '/bm/approvals/leave',
  MANAGER: '/manager/approvals/leave',
  EMPLOYEE: '/employee/leave',
};

const CONTRACTOR_INVOICE_APPROVAL_PATHS: Record<Role, string> = {
  ADMIN: '/admin/dashboard',
  BO: '/bo/approvals/contractor-invoices',
  BM: '/bm/approvals/contractor-invoices',
  MANAGER: '/manager/approvals/contractor-invoices',
  EMPLOYEE: '/employee/leave',
};

const ROLE_ROOTS: Record<Role, string> = {
  ADMIN: '/admin',
  BO: '/bo',
  BM: '/bm',
  MANAGER: '/manager',
  EMPLOYEE: '/employee',
};

function internalPath(link: string | null): string | null {
  if (!link?.startsWith('/') || link.startsWith('//')) return null;
  return link;
}

function isDirectlyAllowedForRole(link: string, role: Role): boolean {
  if (link === '/profile' || link.startsWith('/profile?')) return true;

  const root = ROLE_ROOTS[role];
  return link === root || link.startsWith(`${root}/`);
}

function shiftDetailPath(role: Role, shiftId: string | null): string | null {
  if (!shiftId || role === 'ADMIN') return null;

  switch (role) {
    case 'BO':
      return `/bo/shift/${shiftId}`;
    case 'BM':
      return `/bm/shift/${shiftId}`;
    case 'MANAGER':
      return `/manager/shift/${shiftId}`;
    case 'EMPLOYEE':
      return `/employee/shift/${shiftId}`;
    default:
      return null;
  }
}

function routeFromKnownSection(link: string, role: Role): string | null {
  const lowerLink = link.toLowerCase();

  if (lowerLink.includes('discount')) {
    return role === 'ADMIN' ? '/admin/discount-codes' : dashboardPathForRole(role);
  }

  if (lowerLink.includes('org')) {
    return role === 'ADMIN' ? '/admin/orgs' : dashboardPathForRole(role);
  }

  if (lowerLink.includes('contractor') && lowerLink.includes('invoice')) {
    return CONTRACTOR_INVOICE_APPROVAL_PATHS[role];
  }

  if (lowerLink.includes('invoice')) {
    return INVOICE_PATHS[role];
  }

  if (lowerLink.includes('communication') || lowerLink.includes('message')) {
    return COMMUNICATION_PATHS[role];
  }

  if (lowerLink.includes('leave')) {
    return LEAVE_APPROVAL_PATHS[role];
  }

  if (lowerLink.includes('approval')) {
    return APPROVAL_PATHS[role];
  }

  if (lowerLink.includes('timesheet')) {
    return role === 'EMPLOYEE' ? '/employee/timesheets' : APPROVAL_PATHS[role];
  }

  if (lowerLink.includes('shift')) {
    return SHIFT_LIST_PATHS[role];
  }

  if (lowerLink.includes('job')) {
    return role === 'BO' ? '/bo/jobs' : dashboardPathForRole(role);
  }

  return null;
}

export function notificationTargetPath(notification: Notification, role: Role): string {
  const link = internalPath(notification.link);

  if (link && isDirectlyAllowedForRole(link, role)) {
    return link;
  }

  const shiftPath = shiftDetailPath(role, notification.shift_id);
  if (shiftPath) {
    return shiftPath;
  }

  if (link) {
    const knownSectionPath = routeFromKnownSection(link, role);
    if (knownSectionPath) return knownSectionPath;
  }

  if (notification.type.startsWith('SHIFT_')) {
    return SHIFT_LIST_PATHS[role];
  }

  if (notification.type.startsWith('LEAVE_')) {
    return LEAVE_APPROVAL_PATHS[role];
  }

  if (notification.type.startsWith('COMMUNICATION_')) {
    return COMMUNICATION_PATHS[role];
  }

  return dashboardPathForRole(role);
}
